import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import FilterLinesIcon from "@redotech/redo-icons/filter-lines_filled.svg";
import LayersIcon from "@redotech/redo-icons/layers-two-01.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import {
  AdvancedFilterData,
  FilterClause,
  FilterGroup,
} from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import {
  FilterOperator,
  isFilterGroup,
} from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { deepCopyObject } from "@redotech/util/object";
import classnames from "classnames";
import { memo, useCallback, useMemo } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { AdvancedTableFilter } from "../advanced-filter";
import * as styles from "./filter-group-builder.module.css";

const MAX_NESTING_DEPTH = 3;

export interface FilterGroupBuilderProps {
  filterGroup: FilterGroup;
  onFilterGroupChange: (filterGroup: FilterGroup) => void;
  availableFilters: AdvancedTableFilter[];
  renderFilter: (props: {
    filter: AdvancedTableFilter;
    onFilterChange: (filter: AdvancedTableFilter) => void;
    onRemove: () => void;
    allFilters: AdvancedTableFilter[];
  }) => React.ReactNode;
  onAddFilterClick: (
    onFilterSelected?: (filter: AdvancedFilterData) => void,
  ) => void;
  addFilterButtonRef?: (element: HTMLButtonElement | null) => void;
}

export const FilterGroupBuilder = memo(function FilterGroupBuilder({
  filterGroup,
  onFilterGroupChange,
  availableFilters,
  renderFilter,
  onAddFilterClick,
  addFilterButtonRef,
}: FilterGroupBuilderProps) {
  const { t } = useTranslation();
  // Toggle function that reads current operator from filterGroup prop (always fresh)
  const handleOperatorToggle = useHandler(() => {
    const newOperator =
      filterGroup.operator === FilterOperator.AND
        ? FilterOperator.OR
        : FilterOperator.AND;
    onFilterGroupChange({ ...filterGroup, operator: newOperator });
  });

  const handleClauseChange = useHandler(
    (index: number, newClause: FilterClause) => {
      const newClauses = [...filterGroup.clauses];
      newClauses[index] = newClause;
      onFilterGroupChange({ ...filterGroup, clauses: newClauses });
    },
  );

  const handleClauseRemove = useHandler((index: number) => {
    const newClauses = filterGroup.clauses.filter((_, i) => i !== index);
    onFilterGroupChange({ ...filterGroup, clauses: newClauses });
  });

  const handleAddNestedGroup = useHandler(() => {
    const newGroup: FilterGroup = { operator: FilterOperator.AND, clauses: [] };
    onFilterGroupChange({
      ...filterGroup,
      clauses: [...filterGroup.clauses, newGroup],
    });
  });

  if (filterGroup.clauses.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Text fontSize="sm" textColor="secondary">
          {t("No filters applied")}
        </Text>
        <Flex mt="md">
          <RedoButton
            hierarchy="secondary"
            IconLeading={PlusIcon}
            onClick={() => onAddFilterClick()}
            ref={addFilterButtonRef}
            size="sm"
            text={t("Add filter")}
          />
        </Flex>
      </div>
    );
  }

  return (
    <div className={styles.filterGroupContainer}>
      <FilterGroupClauseList
        addFilterButtonRef={addFilterButtonRef}
        availableFilters={availableFilters}
        currentDepth={0}
        filterGroup={filterGroup}
        onAddFilterClick={onAddFilterClick}
        onAddNestedGroup={handleAddNestedGroup}
        onClauseChange={handleClauseChange}
        onClauseRemove={handleClauseRemove}
        onOperatorToggle={handleOperatorToggle}
        renderFilter={renderFilter}
      />
    </div>
  );
});

interface FilterGroupClauseListProps {
  filterGroup: FilterGroup;
  onOperatorToggle: () => void;
  onClauseChange: (index: number, clause: FilterClause) => void;
  onClauseRemove: (index: number) => void;
  onAddNestedGroup: () => void;
  onAddFilterClick: (
    onFilterSelected?: (filter: AdvancedFilterData) => void,
  ) => void;
  availableFilters: AdvancedTableFilter[];
  renderFilter: (props: {
    filter: AdvancedTableFilter;
    onFilterChange: (filter: AdvancedTableFilter) => void;
    onRemove: () => void;
    allFilters: AdvancedTableFilter[];
  }) => React.ReactNode;
  currentDepth: number;
  addFilterButtonRef?: (element: HTMLButtonElement | null) => void;
}

const FilterGroupClauseList = memo(function FilterGroupClauseList({
  filterGroup,
  onOperatorToggle,
  onClauseChange,
  onClauseRemove,
  onAddNestedGroup,
  onAddFilterClick,
  availableFilters,
  renderFilter,
  currentDepth,
  addFilterButtonRef,
}: FilterGroupClauseListProps) {
  const { t } = useTranslation();
  const canAddNestedGroup = currentDepth < MAX_NESTING_DEPTH - 1;

  // Convert clauses to displayable filters for the renderFilter prop
  const clauseFilters = useMemo(() => {
    return filterGroup.clauses.map((clause) => {
      if (isFilterGroup(clause)) {
        return null;
      }
      const matchingFilter = availableFilters.find(
        (f) => f.data.name === clause.name,
      );
      if (matchingFilter) {
        const clonedFilter = deepCopyObject(matchingFilter);
        clonedFilter.data = clause as AdvancedFilterData;
        return clonedFilter;
      }
      return null;
    });
  }, [filterGroup.clauses, availableFilters]);

  // Display non-group filters before nested groups, preserving original indices
  // for mutations so data order is unchanged
  const sortedClauseIndices = useMemo(() => {
    const indices = filterGroup.clauses.map((_, i) => i);
    return indices.sort((a, b) => {
      const aIsGroup = isFilterGroup(filterGroup.clauses[a]);
      const bIsGroup = isFilterGroup(filterGroup.clauses[b]);
      if (aIsGroup === bIsGroup) {
        return 0;
      }
      return aIsGroup ? 1 : -1;
    });
  }, [filterGroup.clauses]);

  return (
    <div className={styles.filterGroup}>
      {sortedClauseIndices.map((index, displayIndex) => {
        const clause = filterGroup.clauses[index];
        const showOperator = displayIndex > 0;

        if (isFilterGroup(clause)) {
          return (
            <NestedFilterGroupClause
              availableFilters={availableFilters}
              currentDepth={currentDepth}
              filterGroup={clause}
              index={index}
              key={index}
              onAddFilterClick={onAddFilterClick}
              onClauseChange={onClauseChange}
              onClauseRemove={onClauseRemove}
              onParentOperatorToggle={onOperatorToggle}
              operator={filterGroup.operator}
              renderFilter={renderFilter}
              showOperator={showOperator}
            />
          );
        }

        const tableFilter = clauseFilters[index];
        if (!tableFilter) {
          return null;
        }

        return (
          <LeafFilterClause
            allFilters={clauseFilters.filter(
              (f): f is AdvancedTableFilter => f !== null,
            )}
            currentDepth={currentDepth}
            filter={tableFilter}
            index={index}
            key={index}
            onClauseChange={onClauseChange}
            onClauseRemove={onClauseRemove}
            onOperatorToggle={onOperatorToggle}
            operator={filterGroup.operator}
            renderFilter={renderFilter}
            showOperator={showOperator}
          />
        );
      })}
      <Flex gap="sm" mt="xs">
        <RedoButton
          hierarchy="secondary"
          IconLeading={FilterLinesIcon}
          onClick={() => onAddFilterClick()}
          ref={currentDepth === 0 ? addFilterButtonRef : undefined}
          size="xs"
          text={t("Add filter")}
        />
        {canAddNestedGroup && (
          <RedoButton
            hierarchy="secondary"
            IconLeading={LayersIcon}
            onClick={onAddNestedGroup}
            size="xs"
            text={t("Add filter group")}
          />
        )}
      </Flex>
    </div>
  );
});

interface LeafFilterClauseProps {
  filter: AdvancedTableFilter;
  index: number;
  onClauseChange: (index: number, clause: FilterClause) => void;
  onClauseRemove: (index: number) => void;
  operator: FilterOperator;
  showOperator: boolean;
  onOperatorToggle: () => void;
  renderFilter: (props: {
    filter: AdvancedTableFilter;
    onFilterChange: (filter: AdvancedTableFilter) => void;
    onRemove: () => void;
    allFilters: AdvancedTableFilter[];
  }) => React.ReactNode;
  allFilters: AdvancedTableFilter[];
  currentDepth: number;
}

const LeafFilterClause = memo(function LeafFilterClause({
  filter,
  index,
  onClauseChange,
  onClauseRemove,
  operator,
  showOperator,
  onOperatorToggle,
  renderFilter,
  allFilters,
  currentDepth,
}: LeafFilterClauseProps) {
  const handleFilterChange = useCallback(
    (newFilter: AdvancedTableFilter) => {
      onClauseChange(index, newFilter.data);
    },
    [index, onClauseChange],
  );

  const handleRemove = useCallback(() => {
    onClauseRemove(index);
  }, [index, onClauseRemove]);

  return (
    <div className={styles.clauseRow}>
      {showOperator && (
        <FilterOperatorIndicator
          onClick={onOperatorToggle}
          operator={operator}
        />
      )}
      <div className={styles.filterContent}>
        {renderFilter({
          filter,
          onFilterChange: handleFilterChange,
          onRemove: handleRemove,
          allFilters,
        })}
      </div>
    </div>
  );
});

interface NestedFilterGroupClauseProps {
  filterGroup: FilterGroup;
  index: number;
  onClauseChange: (index: number, clause: FilterClause) => void;
  onClauseRemove: (index: number) => void;
  operator: FilterOperator;
  showOperator: boolean;
  onParentOperatorToggle: () => void;
  onAddFilterClick: (
    onFilterSelected?: (filter: AdvancedFilterData) => void,
  ) => void;
  availableFilters: AdvancedTableFilter[];
  renderFilter: (props: {
    filter: AdvancedTableFilter;
    onFilterChange: (filter: AdvancedTableFilter) => void;
    onRemove: () => void;
    allFilters: AdvancedTableFilter[];
  }) => React.ReactNode;
  currentDepth: number;
}

const NestedFilterGroupClause = memo(function NestedFilterGroupClause({
  filterGroup,
  index,
  onClauseChange,
  onClauseRemove,
  operator,
  showOperator,
  onParentOperatorToggle,
  onAddFilterClick,
  availableFilters,
  renderFilter,
  currentDepth,
}: NestedFilterGroupClauseProps) {
  const { t } = useTranslation();
  // Toggle for THIS nested group's operator (reads current value fresh)
  const handleNestedOperatorToggle = useHandler(() => {
    const newOperator =
      filterGroup.operator === FilterOperator.AND
        ? FilterOperator.OR
        : FilterOperator.AND;
    onClauseChange(index, { ...filterGroup, operator: newOperator });
  });

  const handleNestedClauseChange = useHandler(
    (clauseIndex: number, clause: FilterClause) => {
      const newClauses = [...filterGroup.clauses];
      newClauses[clauseIndex] = clause;
      onClauseChange(index, { ...filterGroup, clauses: newClauses });
    },
  );

  const handleNestedClauseRemove = useHandler((clauseIndex: number) => {
    const newClauses = filterGroup.clauses.filter((_, i) => i !== clauseIndex);
    // If the nested group becomes empty, remove it entirely
    if (newClauses.length === 0) {
      onClauseRemove(index);
    } else {
      onClauseChange(index, { ...filterGroup, clauses: newClauses });
    }
  });

  const handleAddNestedGroup = useHandler(() => {
    const newGroup: FilterGroup = { operator: FilterOperator.AND, clauses: [] };
    onClauseChange(index, {
      ...filterGroup,
      clauses: [...filterGroup.clauses, newGroup],
    });
  });

  // For nested groups, open filter picker and add selected filter to this group
  const handleAddFilterToGroup = useHandler(
    (onFilterSelected?: (filter: AdvancedFilterData) => void) => {
      onAddFilterClick((filter) => {
        // Add filter to this nested group
        onClauseChange(index, {
          ...filterGroup,
          clauses: [...filterGroup.clauses, filter],
        });
        onFilterSelected?.(filter);
      });
    },
  );

  // Handler to remove this entire nested group
  const handleRemoveGroup = useHandler(() => {
    onClauseRemove(index);
  });

  return (
    <div className={styles.clauseRow}>
      {showOperator && (
        <FilterOperatorIndicator
          onClick={onParentOperatorToggle}
          operator={operator}
        />
      )}
      <div
        className={classnames(
          styles.filterContent,
          styles.nestedGroup,
          styles[`nestedGroupDepth${currentDepth + 1}`],
          filterGroup.clauses.length === 0 && styles.nestedGroupEmpty,
        )}
      >
        <div className={styles.nestedGroupContent}>
          <FilterGroupClauseList
            availableFilters={availableFilters}
            currentDepth={currentDepth + 1}
            filterGroup={filterGroup}
            onAddFilterClick={handleAddFilterToGroup}
            onAddNestedGroup={handleAddNestedGroup}
            onClauseChange={handleNestedClauseChange}
            onClauseRemove={handleNestedClauseRemove}
            onOperatorToggle={handleNestedOperatorToggle}
            renderFilter={renderFilter}
          />
        </div>
        <button
          className={styles.removeGroupButton}
          onClick={handleRemoveGroup}
          title={t("Remove filter group")}
          type="button"
        >
          <XCloseIcon />
        </button>
      </div>
    </div>
  );
});

interface FilterOperatorIndicatorProps {
  operator: FilterOperator;
  onClick: () => void;
}

const FilterOperatorIndicator = memo(function FilterOperatorIndicator({
  operator,
  onClick,
}: FilterOperatorIndicatorProps) {
  const { t } = useTranslation();
  const isAnd = operator === FilterOperator.AND;

  return (
    <button
      className={classnames(
        styles.operatorToggle,
        isAnd ? styles.operatorAnd : styles.operatorOr,
      )}
      onClick={onClick}
      title={isAnd ? t("Click to switch to OR") : t("Click to switch to AND")}
      type="button"
    >
      <Text fontSize="xxs" fontWeight="semibold">
        {isAnd
          ? t("AND", { context: "filter group operator" })
          : t("OR", { context: "filter group operator" })}
      </Text>
    </button>
  );
});

// Helper function to create an empty filter group
export function createEmptyFilterGroup(): FilterGroup {
  return { operator: FilterOperator.AND, clauses: [] };
}

// Helper function to add a filter to a filter group
export function addFilterToGroup(
  group: FilterGroup,
  filter: AdvancedFilterData,
): FilterGroup {
  return { ...group, clauses: [...group.clauses, filter] };
}

// Helper function to convert flat filters to a filter group
export function filtersToGroup(filters: AdvancedFilterData[]): FilterGroup {
  return { operator: FilterOperator.AND, clauses: filters };
}
